import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import useDocumentAccess from '../../../hooks/useDocumentAccess';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000" || 'http://localhost:8000';

const DocumentManager = ({ employeeId, companyUID, documents, onRefresh }) => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { hasAccess, userRole } = useDocumentAccess(companyUID, employeeId);
  const { t } = useTranslation('myteam');
  const [documentsList, setDocumentsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(6); // Show 6 cards initially
  const [previewingDoc, setPreviewingDoc] = useState(null); // Track which doc is being previewed
  const [signedUrls, setSignedUrls] = useState({}); // Cache signed URLs

  const fetchDocumentEndpoint = async (url) => {
    if (!currentUser) throw new Error(t('documents.signInRequired', 'Sign in to access this document'));
    const token = await currentUser.getIdToken();
    // Document identity always comes from the verified Firebase token; query
    // parameters identify only the tenant, employee and requested document.
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  };

  useEffect(() => {
    if (documents && documents.files) {
      // Convert Firebase object to array and sort by timestamp
      const docsArray = Object.entries(documents.files).map(([id, doc]) => ({
        id,
        ...doc
      }));
      // Sort by uploadedAt descending (newest first)
      docsArray.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
      setDocumentsList(docsArray);
    } else {
      setDocumentsList([]);
    }
  }, [documents]);

  const handleDownload = async (doc) => {
    if (!hasAccess) {
      alert(t('documents.noPermission', 'You do not have permission to download this document'));
      return;
    }

    try {
      // If document is encrypted, use secure download endpoint
      if (doc.encrypted) {
        const downloadUrl = `${BACKEND_URL}/employee/download-document?` + new URLSearchParams({
          companyUID: companyUID,
          employeeId: employeeId,
          documentId: doc.id
        });

        // Use fetch to download and trigger browser download
        const response = await fetchDocumentEndpoint(downloadUrl);
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Download failed');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Legacy unencrypted document - use signed URL or direct download
        if (doc.url) {
          // Check if we have a cached signed URL
          if (signedUrls[doc.id]) {
            window.open(signedUrls[doc.id], '_blank');
            return;
          }

          // Generate signed URL for legacy documents
          try {
            const signedUrlResponse = await fetchDocumentEndpoint(`${BACKEND_URL}/employee/document-signed-url?` + new URLSearchParams({
              companyUID: companyUID,
              employeeId: employeeId,
              documentId: doc.id,
              expirationMinutes: '15'
            }));

            if (signedUrlResponse.ok) {
              const { signedUrl } = await signedUrlResponse.json();
              setSignedUrls(prev => ({ ...prev, [doc.id]: signedUrl }));
              window.open(signedUrl, '_blank');
            } else {
              // Fallback to direct URL (for legacy documents)
              const link = document.createElement('a');
              link.href = doc.url;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              if (!doc.url.toLowerCase().includes('.pdf') && doc.type !== 'contract' && doc.type !== 'nda' && doc.type !== 'policy') {
                link.download = doc.name || 'document';
              }
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          } catch (error) {
            console.error('Failed to get signed URL:', error);
            // Fallback to direct URL
            window.open(doc.url, '_blank');
          }
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert(t('documents.downloadFailed', { message: error.message || 'Unknown error', defaultValue: 'Failed to download document: {{message}}' }));
    }
  };


  const getDocumentTypeLabel = (type) => {
    const key = String(type || 'other').toLowerCase();
    return t(`documents.types.${key}`, key);
  };

  const getDocumentTypeColor = (type) => {
    const normalizedType = String(type || 'other').toLowerCase();
    const colors = {
      'contract': '#568ded',
      'resume': '#7eb0ff',
      'cv': '#7eb0ff',
      'id_card': '#40EB6E',
      'work_permit': '#EBDD40',
      'visa': '#EB8D40',
      'insurance': '#CC40EB',
      'tax_form': '#EB4040',
      'nda': '#4048EB',
      'policy': '#568ded',
      'other': 'rgba(255, 255, 255, 0.3)'
    };
    return colors[normalizedType] || 'rgba(255, 255, 255, 0.3)';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t('documents.unknownDate', 'Unknown date');
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('documents.today', 'Today');
    if (diffDays === 1) return t('documents.yesterday', 'Yesterday');
    if (diffDays < 7) return t('documents.daysAgo', { count: diffDays, defaultValue: '{{count}} days ago' });

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getFilePreview = (doc) => {
    const url = doc.url || '';
    const type = doc.type || 'other';
    const isPdf = url.toLowerCase().includes('.pdf') || type === 'contract' || type === 'nda' || type === 'policy';
    const isPreviewing = previewingDoc === doc.id;

    // For encrypted documents, use signed URL if available
    let previewUrl = url;
    if (doc.encrypted && isPreviewing && signedUrls[doc.id]) {
      previewUrl = signedUrls[doc.id];
    }

    // PDF preview - only load iframe when user clicks to preview (prevents auto-download)
    if (isPdf) {
      if (isPreviewing) {
        // Load iframe only when previewing
        return (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewingDoc(null);
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 10,
                background: 'rgba(0, 0, 0, 0.6)',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--app-text, #FFFFFF)'
              }}
              title={t('documents.closePreview', 'Close preview')}
              data-light-control={"quiet"}>
              <i className="fa-solid fa-times" style={{ fontSize: '12px' }}></i>
            </button>
            {doc.encrypted && !signedUrls[doc.id] ? (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <i className="fa-solid fa-lock" style={{ fontSize: '24px', marginBottom: '8px', color: 'var(--app-tone-40eb6e, #40EB6E)' }}></i>
                  <div style={{ fontSize: '12px', fontFamily: 'Montserrat' }}>{t('documents.loadingEncrypted', 'Loading encrypted document...')}</div>
                </div>
              </div>
            ) : (
              <iframe
                src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                type="application/pdf"
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  border: 'none'
                }}
                title={doc.name}
              />
            )}
          </div>
        );
      } else {
        // Show PDF icon thumbnail instead of loading PDF (prevents auto-download)
        return (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
            borderRadius: '8px',
            color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)',
            cursor: 'pointer'
          }}
            onClick={async (e) => {
              e.stopPropagation();
              // For encrypted docs, get signed URL before previewing
              if (doc.encrypted && !signedUrls[doc.id]) {
                try {
                  const signedUrlResponse = await fetchDocumentEndpoint(`${BACKEND_URL}/employee/document-signed-url?` + new URLSearchParams({
                    companyUID: companyUID,
                    employeeId: employeeId,
                    documentId: doc.id,
                    expirationMinutes: '15'
                  }));
                  if (signedUrlResponse.ok) {
                    const { signedUrl } = await signedUrlResponse.json();
                    setSignedUrls(prev => ({ ...prev, [doc.id]: signedUrl }));
                  }
                } catch (error) {
                  console.error('Failed to get signed URL:', error);
                }
              }
              setPreviewingDoc(doc.id);
            }}
           >
            <i className="fa-solid fa-file-pdf" style={{ fontSize: '48px', marginBottom: '8px', color: 'var(--app-tone-eb4040, #EB4040)' }}></i>
            <span style={{ fontSize: '12px', fontFamily: 'Montserrat' }}>{uiText("Click to preview PDF")}</span>
            {doc.encrypted && (
              <i className="fa-solid fa-lock" style={{ fontSize: '10px', marginTop: '4px', color: 'var(--app-tone-40eb6e, #40EB6E)' }} title={uiText("Encrypted")}></i>
            )}
          </div>
        );
      }
    }

    // Image preview
    if (url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      // For encrypted images, use signed URL if available
      let imageUrl = url;
      if (doc.encrypted) {
        if (signedUrls[doc.id]) {
          imageUrl = signedUrls[doc.id];
        } else {
          // Return placeholder until signed URL is fetched
          return (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
              borderRadius: '8px',
              color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)'
            }}>
              <i className="fa-solid fa-lock" style={{ fontSize: '24px', marginBottom: '8px', color: 'var(--app-tone-40eb6e, #40EB6E)' }}></i>
              <span style={{ fontSize: '12px', fontFamily: 'Montserrat' }}>{uiText("Encrypted Image")}</span>
            </div>
          );
        }
      }

      return (
        <img
          src={imageUrl}
          alt={doc.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: rgba(255,255,255,0.5);"><i class="fa-solid fa-file" style="font-size: 32px;"></i></div>';
          }}
        />
      );
    }

    // Default file icon
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
        borderRadius: '8px',
        color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)'
      }}>
        <i className="fa-solid fa-file" style={{ fontSize: '48px', marginBottom: '8px' }}></i>
        <span style={{ fontSize: '12px', fontFamily: 'Montserrat' }}>{doc.name?.split('.').pop()?.toUpperCase() || uiText("FILE")}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.7 }}>{uiText("Loading documents...")}</div>
    );
  }

  if (!documentsList || documentsList.length === 0) {
    return (
      <div style={{
        color: 'var(--app-text, #FFFFFF)',
        textAlign: 'center',
        padding: '40px 24px',
        fontFamily: 'Montserrat',
        fontSize: '14px',
        borderRadius: '16px',
        border: "1px dashed var(--app-border-strong, rgba(86, 141, 237, 0.28))",
        backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.05))'
      }}>
        <div style={{ opacity: 0.7 }}>{uiText("No documents uploaded yet.")}</div>
      </div>
    );
  }

  const visibleDocuments = documentsList.slice(0, displayCount);
  const hasMore = documentsList.length > displayCount;

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '16px',
        alignItems: 'stretch'
      }}>
        {visibleDocuments.map((doc) => {
          const typeColor = getDocumentTypeColor(doc.type);
          return (
            <div
              key={doc.id}
              style={{
                backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.08))',
                border: "1px solid var(--app-border, rgba(86, 141, 237, 0.15))",
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.12))';
                e.currentTarget.style.borderColor = 'rgba(86, 141, 237, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.08))';
                e.currentTarget.style.borderColor = 'rgba(86, 141, 237, 0.15)';
              }}
              onClick={(e) => {
                // Don't trigger download if clicking on preview area
                if (e.target.closest('.doc-preview-area')) {
                  return;
                }
                handleDownload(doc);
              }}
             >
              {/* Preview */}
              <div
                className="doc-preview-area"
                style={{
                  width: '100%',
                  height: '180px',
                  backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.05))',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {getFilePreview(doc)}

                {/* Overlay on hover */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none'
                }}
                  className="doc-preview-overlay"
                >
                  <i className="fa-solid fa-download" style={{ fontSize: '24px', color: 'var(--app-text, #FFFFFF)' }}></i>
                </div>
              </div>

              {/* Card Content */}
              <div style={{ padding: '12px' }}>
                {/* Document Name */}
                <div style={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--app-text, #FFFFFF)',
                  marginBottom: '8px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {doc.name || uiText("Untitled Document")}
                </div>

                {/* Tags and Timestamp */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  {/* Type Tag */}
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    backgroundColor: `${typeColor}20`,
                    border: `1px solid ${typeColor}40`,
                    width: 'fit-content'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: typeColor
                    }}></div>
                    <span style={{
                      fontFamily: 'Montserrat',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: typeColor,
                      textTransform: 'none',
                      letterSpacing: '0.5px'
                    }}>
                      {getDocumentTypeLabel(doc.type || uiText("other"))}
                    </span>
                  </div>

                  {/* Timestamp */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '11px',
                    color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)'
                  }}>
                    <i className="fa-solid fa-clock" style={{ fontSize: '10px' }}></i>
                    <span>{formatDate(doc.uploadedAt)}</span>
                  </div>

                  {/* Encryption Indicator */}
                  {doc.encrypted && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontFamily: 'Montserrat',
                      fontSize: '10px',
                      color: 'var(--app-tone-40eb6e, #40EB6E)',
                      marginTop: '4px'
                    }}>
                      <i className="fa-solid fa-lock" style={{ fontSize: '8px' }}></i>
                      <span>{uiText("Encrypted")}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: "1px solid var(--app-border, rgba(86, 141, 237, 0.15))"
                }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => handleDownload(doc)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.15))',
                      border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                      borderRadius: '6px',
                      color: 'var(--app-text, #FFFFFF)',
                      fontFamily: 'Montserrat',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.25))';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.15))';
                    }}
                    data-light-control={"quiet"}>
                    <i className="fa-solid fa-download"></i>{uiText("Download")}</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px'
        }}>
          <button
            onClick={() => setDisplayCount(prev => prev + 6)}
            style={{
              padding: '10px 24px',
              backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.15))',
              border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
              borderRadius: '8px',
              color: 'var(--app-text, #FFFFFF)',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.25))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.15))';
            }}
            data-light-control={"quiet"}>{uiText("Load More (")}{documentsList.length - displayCount}{uiText(" remaining)")}</button>
        </div>
      )}

      {/* CSS for hover overlay */}
      <style>{`
        .doc-preview-overlay {
          opacity: 0;
        }
        div:hover .doc-preview-overlay {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default DocumentManager;
